import { faChildren, faCreditCard, faCube, faLayerGroup, faLocationDot, faUserGear } from '@fortawesome/free-solid-svg-icons';

// Managing the users of a school: shared by the Leerkrachten/Begeleiders page and the BSO flow.
// Rights are only changed through these Cloud Functions; the Firestore rules block direct writes.

export function colleaguesLabel(isBso) {
  return isBso ? 'Begeleiders' : 'Leerkrachten';
}

// Leerlingen at a school, kinderen at a BSO
export function studentsLabel(isBso) {
  return isBso ? 'Kinderen' : 'Leerlingen';
}

export function studentLabel(isBso) {
  return isBso ? 'Kind' : 'Leerling';
}

// Not "locatie": some customers use groups for their locations
export function organisationLabel(isBso) {
  return isBso ? 'bso' : 'school';
}

// Loaded on demand: App.vue imports this file for its labels, the Functions SDK isn't needed at startup
async function call(name, data) {
  const { functions, httpsCallable } = await import('@/firebaseServices');
  return httpsCallable(functions, name)(data).then((result) => result.data);
}

// schoolId: the school in the store; the server only accepts another one from Yuha admins
export const listSchoolUsers = (schoolId) => call('ListSchoolUsers', { schoolId });
// deviceIds: the invitee receives those (just sent) headsets, mentioned in the e-mail
export const inviteSchoolUser = (schoolId, { name, email, permissions, deviceIds = null }) => call('InviteSchoolUser', { schoolId, name, email, permissions, deviceIds });
export const resendSchoolInvitation = (schoolId, invitationId) => call('ResendSchoolInvitation', { schoolId, invitationId });
export const revokeSchoolInvitation = (schoolId, invitationId) => call('RevokeSchoolInvitation', { schoolId, invitationId });
export const updateSchoolUserPermissions = (schoolId, userId, permissions) => call('UpdateSchoolUserPermissions', { schoolId, userId, permissions });
export const removeSchoolUser = (schoolId, userId) => call('RemoveSchoolUser', { schoolId, userId });
export const getSchoolInvitation = (token, email) => call('GetSchoolInvitation', { token, ...(email ? { email } : {}) });
export const acceptSchoolInvitation = (data) => call('AcceptSchoolInvitation', data);

export const EMPTY_PERMISSIONS = {
  manageStudents: false,
  manageGroups: false,
  manageSubscription: false,
  manageUsers: false,
  manageLocations: false,
  contentCreator: false,
  showAllGroups: false,
  groupIds: [],
  showAllLocations: true,
  locationIds: []
};

// Rights as stored on users/{uid}; users from before the permission fields may manage children and groups
export function permissionsOf(userData = {}) {
  const has = (key) => Object.prototype.hasOwnProperty.call(userData, key);
  const manageStudents = has('manageStudents') ? userData.manageStudents === true : true;
  const manageGroups = has('manageGroups') ? userData.manageGroups === true : manageStudents;
  return {
    manageStudents,
    manageGroups,
    manageSubscription: userData.manageSubscription === true,
    manageUsers: userData.manageUsers === true,
    manageLocations: userData.manageLocations === true,
    contentCreator: userData.contentCreator === true
  };
}

// One icon, colour and tooltip per right
function rightBadges(isBso) {
  const children = isBso ? 'kinderen' : 'leerlingen';
  return {
    students: { key: 'students', icon: faChildren, tone: 'green', label: `Beheert ${children}` },
    groups: { key: 'groups', icon: faLayerGroup, tone: 'blue', label: `Beheert groepen en ${children}` },
    billing: { key: 'billing', icon: faCreditCard, tone: 'yellow', label: 'Beheert facturatie' },
    users: { key: 'users', icon: faUserGear, tone: 'pink', label: 'Beheert gebruikers' },
    locations: { key: 'locations', icon: faLocationDot, tone: 'navy', label: 'Beheert locaties' },
    content: { key: 'content', icon: faCube, tone: 'plum', label: 'Content creator: maakt lessen' }
  };
}

// Small icons that show at a glance what someone may manage; groups include children
export function permissionBadges(permissions, isBso) {
  const all = rightBadges(isBso);
  const badges = [];
  if (permissions.manageGroups) badges.push(all.groups);
  else if (permissions.manageStudents) badges.push(all.students);
  if (permissions.manageSubscription) badges.push(all.billing);
  if (permissions.manageUsers) badges.push(all.users);
  if (permissions.manageLocations) badges.push(all.locations);
  if (permissions.contentCreator) badges.push(all.content);
  return badges;
}

// Whether two sets of rights are the same (group order does not matter)
export function samePermissions(a, b) {
  const flags = ['manageStudents', 'manageGroups', 'manageSubscription', 'manageUsers', 'manageLocations', 'contentCreator', 'showAllGroups'];
  if (flags.some((flag) => Boolean(a[flag]) !== Boolean(b[flag]))) return false;
  if ((a.showAllLocations !== false) !== (b.showAllLocations !== false)) return false;
  return sameIds(a.groupIds, b.groupIds) && sameIds(a.locationIds, b.locationIds);
}

function sameIds(a = [], b = []) {
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.length === sortedB.length && sortedA.every((id, index) => id === sortedB[index]);
}

// Same rules as the server: group management needs child management and sees every group
export function withImplications(permissions) {
  const result = {
    ...permissions,
    groupIds: [...(permissions.groupIds || [])],
    showAllLocations: permissions.showAllLocations !== false,
    locationIds: [...(permissions.locationIds || [])]
  };
  if (result.manageGroups) {
    result.manageStudents = true;
    result.showAllGroups = true;
  }
  if (result.showAllGroups) result.groupIds = [];
  if (result.manageLocations) result.showAllLocations = true;
  if (result.showAllLocations) result.locationIds = [];
  return result;
}

// Dutch text for the errors of these functions; the server sends English messages or codes
const MESSAGES = {
  'already-in-school': 'Deze persoon heeft al toegang tot deze {organisation}.',
  'already-in-other-school': 'Dit e-mailadres hoort al bij een andere school of bso. Neem contact op met Yuha om het over te zetten.',
  'last-user-manager': 'Er moet minstens één persoon overblijven die gebruikers mag beheren.',
  'outside-location-scope': 'Deze persoon werkt (ook) op een locatie die jij niet beheert.',
  'Location not found': 'Deze locatie bestaat niet meer. Ververs de pagina.',
  'group-not-at-location': 'Die groep hoort niet (meer) bij deze locatie. Ververs de pagina.',
  'invitation-not-open': 'Deze uitnodiging is niet meer geldig. Vraag om een nieuwe uitnodiging.',
  'wrong-account': 'Je bent ingelogd met een ander e-mailadres dan waarvoor de uitnodiging is.',
  'account-exists': 'Er bestaat al een account met dit e-mailadres. Log in om de uitnodiging te accepteren.',
  'email is not valid': 'Vul een geldig e-mailadres in.',
  'password must be at least 6 characters': 'Het wachtwoord moet minstens 6 tekens hebben.',
  'agreementAccepted must be true': 'Ga akkoord met de voorwaarden om verder te gaan.',
  'Failed to send the invitation e-mail': 'De uitnodiging kon niet verstuurd worden. Probeer het opnieuw.'
};

const CODE_MESSAGES = {
  'permission-denied': 'Je hebt geen rechten om dit te doen.',
  'not-found': 'Dit kon niet gevonden worden. Ververs de pagina en probeer het opnieuw.',
  'unauthenticated': 'Je bent niet meer ingelogd. Log opnieuw in en probeer het nog een keer.',
  'unavailable': 'Geen verbinding met de server. Controleer je internet en probeer het opnieuw.'
};

export function schoolUsersErrorMessage(error, isBso = false) {
  const code = typeof error?.code === 'string' ? error.code.replace(/^functions\//, '') : '';
  const message = MESSAGES[error?.message] || CODE_MESSAGES[code] || 'Er ging iets mis. Probeer het opnieuw.';
  return message.replace('{organisation}', organisationLabel(isBso));
}
